import QRCode from "react-qr-code";
import { SITE, STORY, PRIZES, DONORS } from "@/config";

export default function Poster() {
  // Poster QR always points at the LIVE site — posters get printed from
  // wherever the page is opened, so this must never be the preview address.
  const siteUrl = "https://keighreycivil.net";

  return (
    <div data-testid="poster-page" className="min-h-screen bg-[#ECEFF1] py-8 px-4 flex flex-col items-center print:bg-white print:p-0">
      {/* On-screen print tip — hidden when printed */}
      <p data-testid="poster-print-tip" className="print:hidden mb-6 max-w-[210mm] text-center text-xs text-[#546E7A]">
        Print tip: use your browser's Print → "Save as PDF" or print directly (A4 portrait, margins: none, background graphics: on).
        The QR code points to keighreycivil.net no matter where you print from.
      </p>

      {/* A4 sheet */}
      <div
        data-testid="poster-sheet"
        className="w-full max-w-[210mm] min-h-[297mm] bg-white shadow-2xl print:shadow-none flex flex-col print:w-[210mm]"
        style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
      >
        {/* Emotional hero: family photo, title beneath */}
        <div className="relative h-[98mm] overflow-hidden">
          <img
            data-testid="poster-family-photo"
            src={SITE.heroPhoto}
            alt="Casey and her family"
            className="absolute inset-0 w-full h-full object-cover object-[50%_32%]"
          />
        </div>

        <div className="flex-1 flex flex-col px-[14mm] pb-[10mm]">
          <div className="text-center mt-5">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C77B7B]">Charity raffle</p>
            <h1 className="mt-1.5 font-serif-display text-[40px] leading-[1.1] text-[#333333]">
              Help Give Mum More Tomorrows
            </h1>
          </div>
          {/* Tear-jerker — short and simple */}
          <p className="mt-4 text-center font-serif-display italic text-[19px] leading-relaxed text-[#333333]">
            "All I want is more time with my boys."
          </p>
          <p className="mt-3 text-center text-[12px] leading-relaxed text-[#546E7A] max-w-[150mm] mx-auto">
            Casey is a young mum fighting aggressive blood cancer. Her only chance is a stem cell
            transplant — months in hospital, away from her two little boys. <strong className="text-[#333333]">Every ticket
            helps keep this family together.</strong>
          </p>

          {/* What's on offer — simple strip */}
          <div className="mt-6 rounded-2xl border border-[#DDE1E4] bg-[#FAFBFC] px-6 py-4">
            <p className="text-center text-[10px] uppercase tracking-[0.3em] text-[#C77B7B]">What you could win</p>
            <div className="mt-3 grid grid-cols-3 gap-4 text-center">
              {PRIZES.items.map((p) => (
                <div key={p.place} data-testid={`poster-prize-${p.place}`}>
                  <p className="font-serif-display italic text-2xl text-[#C77B7B] leading-none">{p.place}</p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#333333]">{p.title}</p>
                  <p className="text-[9.5px] text-[#546E7A]">{p.value}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 pt-3 border-t border-[#DDE1E4] text-center text-[10px] text-[#546E7A]">
              Ten prizes across three draws — all donated by local businesses. 100% of proceeds go to Casey and her family.
            </p>
          </div>

          {/* QR band — the call to action */}
          <div className="mt-6 rounded-2xl bg-[#333333] text-[#F5F5F5] px-8 py-6 flex items-center gap-8">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C77B7B]">Buy a ticket</p>
              <div className="mt-2 flex items-end gap-5">
                <div>
                  <p className="font-serif-display text-4xl">$20</p>
                  <p className="text-[11px] text-[#B0BEC5]">single</p>
                </div>
                <div className="pb-1 text-[#546E7A] font-serif-display italic text-xl">or</div>
                <div>
                  <p className="font-serif-display text-4xl">$50</p>
                  <p className="text-[11px] text-[#B0BEC5]">bundle of 3</p>
                </div>
              </div>
              <p className="mt-3 text-[12px] leading-relaxed text-[#DDE1E4]">
                Scan the code with your phone camera to buy securely online.
                Your ticket number is emailed to you the moment payment is approved.
              </p>
              <p className="mt-3 text-[13px] text-[#DDE1E4]">
                or visit <span data-testid="poster-alt-url" className="font-serif-display italic text-xl text-[#E8C4C4]">keighreycivil.net</span>
              </p>
            </div>
            <div className="text-center shrink-0">
              <div className="p-3.5 bg-white rounded-2xl inline-block shadow-lg">
                <QRCode data-testid="poster-qr" value={siteUrl} size={118} bgColor="#FFFFFF" fgColor="#333333" />
              </div>
              <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#C77B7B]">Scan to buy</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-5">
            <div className="flex items-center justify-center gap-5 flex-wrap opacity-90">
              {DONORS.logos.map((d) => (
                <img key={d.name} src={d.src} alt={d.name} className={`h-6 w-auto object-contain ${d.dark ? "rounded" : ""}`} />
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[#DDE1E4] flex items-center justify-between text-[10px] text-[#8a8a8a]">
              <span>{STORY.signoff}</span>
              <span data-testid="poster-url">{siteUrl.replace(/^https?:\/\//, "")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
